const STORAGE_KEY = "brightcall_attribution_v1";
const ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000;

const emptyAttribution = () => ({
  gclid: "",
  gbraid: "",
  wbraid: "",
  campaign_id: "",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
  utm_term: "",
  utm_content: "",
  landing_page: "",
  referrer: "",
  captured_at: 0,
});

const clean = (value, maxLength = 2000) =>
  String(value || "").trim().slice(0, maxLength);

const getFirstParameter = (params, names) => {
  for (const name of names) {
    const value = params.get(name);
    if (value) return value;
  }

  return "";
};

const getCampaignId = (params) =>
  getFirstParameter(params, [
    "campaign_id",
    "campaignid",
    "utm_id",
    "gad_campaignid",
  ]);

// Google normally sends lowercase `gbraid` and `wbraid`. URL parameter
// names are case-sensitive, so the aliases also support manually created
// test URLs such as `Wbraid=...` without changing the identifier value.
const getGbraid = (params) =>
  getFirstParameter(params, ["gbraid", "GBRAID", "Gbraid"]);

const getWbraid = (params) =>
  getFirstParameter(params, ["wbraid", "WBRAID", "Wbraid"]);

const getExternalDocumentReferrer = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "";
  }

  const rawReferrer = clean(document.referrer, 2000);

  if (!rawReferrer) {
    return "";
  }

  try {
    const referrerUrl = new URL(rawReferrer);

    // Do not treat navigation from another page on the same website as the
    // acquisition referrer.
    if (referrerUrl.origin === window.location.origin) {
      return "";
    }

    return clean(referrerUrl.href, 2000);
  } catch (error) {
    return rawReferrer;
  }
};

const readAttributionFromBrowser = () => {
  if (typeof window === "undefined") return emptyAttribution();

  const params = new URLSearchParams(window.location.search || "");

  return {
    gclid: clean(getFirstParameter(params, ["gclid", "GCLID", "Gclid"]), 255),
    gbraid: clean(getGbraid(params), 1000),
    wbraid: clean(getWbraid(params), 1000),
    campaign_id: clean(getCampaignId(params), 255),
    utm_source: clean(params.get("utm_source"), 255),
    utm_medium: clean(params.get("utm_medium"), 255),
    utm_campaign: clean(params.get("utm_campaign"), 500),
    utm_term: clean(params.get("utm_term"), 500),
    utm_content: clean(params.get("utm_content"), 500),
    landing_page: clean(window.location.href.split("#")[0], 2000),
    referrer: getExternalDocumentReferrer(),
    captured_at: Date.now(),
  };
};

const hasCampaignAttribution = (data) =>
  Boolean(
    data.gclid ||
      data.gbraid ||
      data.wbraid ||
      data.campaign_id ||
      data.utm_source ||
      data.utm_medium ||
      data.utm_campaign ||
      data.utm_term ||
      data.utm_content
  );

const readStoredAttribution = () => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const capturedAt = Number(parsed?.captured_at || 0);

    if (!capturedAt || Date.now() - capturedAt > ATTRIBUTION_TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return { ...emptyAttribution(), ...parsed };
  } catch (error) {
    console.warn("Unable to read BrightCall attribution:", error);
    return null;
  }
};

/**
 * Stores the first visit and replaces it only when a later visit contains
 * new campaign/click parameters. Run this globally in gatsby-browser.js so
 * attribution is captured before the visitor navigates to a form page.
 */
export const captureBrightcallAttribution = () => {
  if (typeof window === "undefined") return emptyAttribution();

  const current = readAttributionFromBrowser();
  const stored = readStoredAttribution();

  const shouldStore = !stored || hasCampaignAttribution(current);
  const attribution = shouldStore ? current : stored;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch (error) {
    console.warn("Unable to store BrightCall attribution:", error);
  }

  return attribution;
};

export const getBrightcallAttribution = () => {
  if (typeof window === "undefined") return emptyAttribution();
  return readStoredAttribution() || captureBrightcallAttribution();
};

/**
 * document.referrer is legitimately empty for direct, bookmarked, pasted,
 * privacy-restricted, and some app/browser visits. This function keeps the
 * BrightCall custom parameter non-empty without pretending a URL existed.
 */
export const getBrightcallReferrerValue = (attribution) => {
  const actualReferrer = clean(attribution?.referrer, 2000);

  if (actualReferrer) {
    return actualReferrer;
  }

  if (
    clean(attribution?.gclid, 255) ||
    clean(attribution?.gbraid, 1000) ||
    clean(attribution?.wbraid, 1000)
  ) {
    return "google-ads";
  }

  const utmSource = clean(attribution?.utm_source, 255);

  if (utmSource) {
    return `utm:${utmSource}`;
  }

  return "direct";
};

export const createBrightcallLeadId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `lead_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
};
